/**
 * @file team-invite.schema.ts
 * @description Convite para entrar na equipe de uma marca — ou para governar
 * o ecossistema.
 *
 * ## Por que substitui a criação direta de conta
 *
 * Até 9/09/2026 o mantenedor nomeava digitando o e-mail e, se a conta não
 * existisse, **escolhia uma senha provisória para outra pessoa**. Três
 * problemas, do menor para o maior:
 *
 *   1. A tela dizia se o e-mail já tinha conta ("A pessoa ainda não tem
 *      conta", e um 404 quando tinha). Isso é enumeração de contas.
 *   2. Quem nomeava conhecia a senha. `forcePasswordChange` obriga a trocar no
 *      primeiro acesso, mas não impede que o primeiro acesso seja de quem
 *      nomeou.
 *   3. Ninguém provava controle do endereço. Bastava digitar o e-mail de
 *      alguém para criar uma conta com aquele endereço e um papel
 *      administrativo dentro.
 *
 * Com convite, quem aceita prova que recebeu o e-mail e escolhe a própria
 * senha. Quem convida nunca toca em credencial.
 *
 * ## Por que um documento, e não um JWT
 *
 * O convite de acesso compartilhado (`shared-access.routes.ts`) é um JWT
 * assinado, sem documento. Funciona para "mande um link", mas não responde
 * "já aceitou?", não permite revogar, e reenviar não invalida o anterior.
 * Aqui as três coisas são requisito.
 */
import { z } from 'zod';
import { marcaDoVinculo } from './brand';
import { BrandRoleEnum } from './membership.schema';

/**
 * 48 horas. Deliberadamente frouxo para um primeiro momento: o custo de um
 * convite vencido é reenviar, e o custo de uma janela curta demais é a pessoa
 * não conseguir entrar na sexta à noite.
 *
 * É também o que limita a exposição do token, que não é pequena: o provedor
 * de e-mail reescreve todo link do HTML para contar cliques, e a Brevo não
 * permite desligar isso em transacional (a opção de "anonimizar" anonimiza
 * quem clicou, não impede a reescrita). Então o token transita pelo
 * redirecionador dela e fica nos logs de um terceiro.
 *
 * Risco aceito em 9/09/2026, com estas três contenções: janela de 48h, uso
 * único, e revogação a qualquer momento. Se um dia deixar de ser aceitável, a
 * saída é tirar o segredo da URL — código digitado em vez de link.
 */
export const HORAS_DE_VALIDADE_DO_CONVITE = 48;

/**
 * `EXPIRED` não existe aqui de propósito: expiração é `expiresAt < agora`
 * sobre um convite `PENDING`, calculada na leitura.
 *
 * Gravar o estado exigiria um job para virá-lo, e até ele rodar o mesmo
 * convite seria "pendente" no banco e "expirado" na verdade — duas respostas
 * para a mesma pergunta.
 */
export const TeamInviteStatusEnum = z.enum(['PENDING', 'ACCEPTED', 'REVOKED']);
export type TeamInviteStatus = z.infer<typeof TeamInviteStatusEnum>;

export const TeamInviteSchema = z.preprocess(
  (raw) => {
    if (!raw || typeof raw !== 'object') return raw;
    const v = raw as Record<string, unknown>;
    return { ...v, brandId: marcaDoVinculo(v.role, v.brandId) };
  },
  z.object({
  id: z.string(),

  /** Sempre minúsculo: é a chave de busca e de comparação. */
  email: z.string().email(),

  /** A marca onde o papel vale. `null` para MAINTAINER (A044). */
  brandId: z.string().min(1).nullable(),

  role: BrandRoleEnum,

  /**
   * SHA-256 do token, nunca o token.
   *
   * Mesma razão de `passwordHash`: quem lê o banco não deve conseguir aceitar
   * convite de ninguém. O token só existe no e-mail enviado.
   */
  tokenHash: z.string(),

  status: TeamInviteStatusEnum.default('PENDING'),

  expiresAt: z.coerce.date(),

  /** Quem convidou. Serve para auditar "quem autorizou este acesso?". */
  invitedByAccountId: z.string(),

  /**
   * Convite nascido de um link aberto (A037): guarda o link e o que ele
   * pedia na hora. `requerValidacao` decide como o vínculo nasce no aceite —
   * aguardando aprovação ou ativo sem validação. Ausente = convite por e-mail
   * de sempre, que nasce validado por quem convidou.
   */
  viaLinkId: z.string().nullable().optional(),
  requerValidacao: z.boolean().optional(),

  acceptedAt: z.coerce.date().nullable().default(null),
  acceptedByAccountId: z.string().nullable().default(null),
  /** Quando foi revogado. Ausente em revogação anterior a isto: vale `updatedAt`. */
  revokedAt: z.coerce.date().nullable().optional(),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  })
);

export type TeamInvite = z.infer<typeof TeamInviteSchema>;

/** O status que a tela mostra — inclui o expirado, que não é gravado. */
export type StatusDeConvite = TeamInviteStatus | 'EXPIRED';

export function statusDoConvite(convite: Pick<TeamInvite, 'status' | 'expiresAt'>, agora = new Date()): StatusDeConvite {
  if (convite.status !== 'PENDING') return convite.status;
  return convite.expiresAt.getTime() < agora.getTime() ? 'EXPIRED' : 'PENDING';
}

/** Quanto tempo um convite encerrado ainda aparece na lista. */
export const DIAS_NA_LISTA_DEPOIS_DE_ENCERRADO = 7;

/**
 * O convite ainda aparece na lista?
 *
 * Aceito sai na hora — virou vínculo, e é na lista de vínculos que ele mora.
 * Revogado ou expirado fica mais 7 dias, para quem olhar entender o que
 * aconteceu, e depois sai — senão a lista vira histórico. Vale para toda
 * lista que tem a seção de convites: equipe, profissionais, mantenedores.
 */
export function conviteAindaAparece(
  convite: Pick<TeamInvite, 'status' | 'expiresAt' | 'updatedAt'> & { revokedAt?: Date | null },
  agora = new Date()
): boolean {
  const limite = DIAS_NA_LISTA_DEPOIS_DE_ENCERRADO * 24 * 60 * 60 * 1000;
  const status = statusDoConvite(convite, agora);
  if (status === 'ACCEPTED') return false;
  if (status === 'REVOKED') return (convite.revokedAt ?? convite.updatedAt).getTime() + limite > agora.getTime();
  if (status === 'EXPIRED') return convite.expiresAt.getTime() + limite > agora.getTime();
  return true;
}

export const STATUS_DE_CONVITE_LABELS: Record<StatusDeConvite, string> = {
  PENDING: 'Aguardando aceite',
  ACCEPTED: 'Aceito',
  REVOKED: 'Revogado',
  EXPIRED: 'Expirado',
};
